import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type ExpenseRequestStatus =
  | 'DRAFT'
  | 'SUPERVISOR_REVIEW'
  | 'WAITING_PAYMENT'
  | 'WAITING_CLEARING'
  | 'ACCOUNTING_REVIEW_RECEIPT'
  | 'VERIFIED'
  | 'PAID'
  | 'REJECTED'
  | 'CANCELLED'
  | 'DELETED';

export type ExpenseRequestSource = 'BOOKING_ESTIMATE' | 'TOUR_ESTIMATE' | 'MANUAL' | 'REFUND';
export type ExpenseRequestBookingType = 'JOIN_TOUR' | 'GROUP_TOUR';
export type ExpenseRequestTargetType = 'BOOKING' | 'TOUR' | 'GENERAL' | 'REFUND';

export interface ExpenseRequestItem {
  id: string;
  expenseNo: string;
  targetType: ExpenseRequestTargetType;
  bookingId: string | null;
  bookingNo: string;
  bookingType: ExpenseRequestBookingType | null;
  customerName: string;
  tourId: string | null;
  tourNo: string;
  tourCode: string;
  tourName: string;
  country: string;
  requestedById: string;
  requestedByName: string;
  expenseTypeId: string | null;
  expenseTypeName: string | null;
  expenseNameSnapshot: string;
  source: ExpenseRequestSource;
  wholesaleId: string | null;
  wholesaleName: string | null;
  wholesaleNameSnapshot: string | null;
  description: string;
  amount: number | null;
  actualTransferAmount: number | null;
  attachmentPath: string | null;
  receiptUrl: string | null;
  receiptPath: string | null;
  receiptSubmittedAt: string | null;
  status: ExpenseRequestStatus;
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExpenseRequestFilters {
  dateFrom: string;
  dateTo: string;
  status: 'ALL' | ExpenseRequestStatus;
  bookingType: 'ALL' | ExpenseRequestBookingType;
  targetType: 'ALL' | ExpenseRequestTargetType;
  expenseTypeId: string;
  wholesaleId: string;
}

export interface ExpenseRequestFilterOptions {
  expenseTypes: Array<{ id: string; name: string }>;
  wholesales: Array<{ id: string; name: string }>;
}

interface ExpenseRequestResponse {
  items: ExpenseRequestItem[];
  filterOptions: ExpenseRequestFilterOptions;
}

@Injectable({ providedIn: 'root' })
export class ExpenseRequestService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/expense-requests`;

  async getItems(filters: ExpenseRequestFilters): Promise<ExpenseRequestResponse> {
    let params = new HttpParams()
      .set('status', filters.status)
      .set('bookingType', filters.bookingType)
      .set('targetType', filters.targetType)
      .set('expenseTypeId', filters.expenseTypeId)
      .set('wholesaleId', filters.wholesaleId);

    if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params = params.set('dateTo', filters.dateTo);

    return firstValueFrom(
      this.http.get<ExpenseRequestResponse>(this.apiUrl, {
        params,
        withCredentials: true,
      }),
    );
  }

  resolveFileUrl(fileUrl: string): string {
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    if (/^https?:\/\//i.test(environment.apiUrl)) {
      return `${new URL(environment.apiUrl).origin}${fileUrl}`;
    }
    return fileUrl;
  }
}
